import { fetchAuthSession } from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import { type ReactElement, type ReactNode, useCallback, useEffect, useState } from "react";

import { type AuthUser, getCurrentUser, signIn, signOut } from "./auth";
import { AuthContext } from "./auth-context";
import { LoadingScreen } from "./LoadingScreen";
import { LoginPage } from "./LoginPage";

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps): ReactElement {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSignedOut, setIsSignedOut] = useState(false);

  const checkSession = useCallback(async (): Promise<void> => {
    try {
      const session = await fetchAuthSession();
      if (!session.tokens?.accessToken) {
        setUser(null);
        return;
      }
      const currentUser = await getCurrentUser();
      setUser(currentUser);
    } catch {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void checkSession();
  }, [checkSession]);

  useEffect(() => {
    const unsubscribe = Hub.listen("auth", ({ payload }) => {
      switch (payload.event) {
        case "signedIn":
          setIsSignedOut(false);
          void checkSession();
          break;
        case "signedOut":
          setIsSignedOut(true);
          setUser(null);
          break;
      }
    });
    return unsubscribe;
  }, [checkSession]);

  if (isLoading) return <LoadingScreen />;

  if (!user) {
    return <LoginPage isSignedOut={isSignedOut} onSignIn={signIn} />;
  }

  const logout = (): void => {
    void signOut();
  };

  return <AuthContext.Provider value={{ user, logout }}>{children}</AuthContext.Provider>;
}
